import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, Loader2, Package, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { productService } from '../../services/productService';
import { fetchQuantitaRese } from '../../services/rmaService';
import { AddToRMAModal } from '../../components/rma/AddToRMAModal';
import { RMAProductCard } from '../../components/rma/RMAProductCard';
import { useRMACart } from '../../context/RMACartContext';
import { usePublicConfig } from '../../hooks/usePublicConfig';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { codiceOemDaMostrare } from '../../utils/oemCodes';

/** Formatta una data (ISO o parsabile) in gg/mm/aaaa. */
function fmtIt(d: string): string {
  return new Date(d).toLocaleDateString('it-IT');
}

interface SearchResultRow {
  sku: string;
  dtkCodice: string;
  title: string;
  brand: string;
  category: string;
  codiceOem: string;
  docTipork: string;
  docTipoLabel: string;
  docAnno: number;
  docSerie: string;
  docNumdoc: number;
  docKey: string;
  /** Numero mostrato: la fattura se il D.D.T. è fatturato, sennò il D.D.T. */
  docNumeroLabel: string;
  orderId: string;
  orderDate: string;
  qtyDelivered: number;
  unitPrice: number;
}

export default function RMASearchPage() {
  const [query, setQuery] = useState('');
  const [modalProduct, setModalProduct] = useState<any>(null);
  const { items: rmaItems } = useRMACart();
  const [results, setResults] = useState<SearchResultRow[]>([]);
  const [quantitaReseMap, setQuantitaReseMap] = useState<Record<string, Record<string, number>>>({});
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // Un guasto del gestionale e "non hai acquistato questo articolo" davano la
  // stessa schermata: l'errore veniva inghiottito e restava una lista vuota.
  const [searchError, setSearchError] = useState<string | null>(null);
  // Data dell'acquisto più recente scartato perché fuori finestra: permette
  // di spiegare il vuoto invece di limitarsi a dichiararlo.
  const [ultimoFuoriFinestra, setUltimoFuoriFinestra] = useState<string | null>(null);
  // I documenti degli ultimi 12 mesi arrivano dal gestionale la prima volta:
  // la ricerca aspetta il Caricamento e lo dice.
  const [caricandoAcquisti, setCaricandoAcquisti] = useState(false);

  const { rma_return_window_months: finestraMesi } = usePublicConfig();

  // Autocompletamento catalogo (richiesta utente giugno 2026)
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);
  const skipSuggestRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipSuggestRef.current) { skipSuggestRef.current = false; return; }
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); setSuggestOpen(false); return; }

    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const resp = await productService.list({ q, limit: 7, publicOnly: true, codeOnly: true, expandOemMatches: true });
        if (myReqId !== reqIdRef.current) return; // risposta obsoleta
        setSuggestions(resp.items);
        setSuggestOpen(true);
        setSelectedIdx(-1);
      } catch {
        if (myReqId === reqIdRef.current) setSuggestions([]);
      } finally {
        if (myReqId === reqIdRef.current) setSuggestLoading(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setSuggestOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /** Fase 2 della ricerca: dati dei prodotti di catalogo già risolti,
   * chiede al backend i documenti del cliente da cui può rendere quegli
   * articoli (stesse regole del Reso da Documenti, 12 mesi fissi) e
   * costruisce le righe di risultato. Quantità e prezzo sono sempre quelli
   * reali della spedizione. Se i documenti dell'anno non sono ancora in
   * cache, il backend li carica dal gestionale: si richiede finché arrivano.
   *
   * Separata da ``runSearch`` perché il click su un suggerimento ha già il
   * prodotto in mano e non deve ricercarlo nel catalogo: vedi
   * ``pickSuggestion``. ``cercato`` è il testo della ricerca libera: se
   * colpisce un Codice OEM, la riga mostra quel codice. */
  const loadMovimenti = async (products: any[], cercato?: string) => {
    setSuggestOpen(false);
    setLoading(true);
    setSearched(true);
    setSearchError(null);
    setUltimoFuoriFinestra(null);

    try {
      let erpNonRaggiungibile = false;
      let fuoriFinestraServer: string | null = null;
      const chiedi = () => Promise.all(products.map(async (p: any) => {
        try {
          const r = await api.get('/api/clienti/me/movimenti-articolo', { params: { sku: p.sku } });
          return { product: p, movimenti: r.data.movimenti as any[], caricamento: r.data.caricamento,
            fuori: (r.data.ultimo_fuori_finestra as string | null) ?? null };
        } catch (e: any) {
          // 503 = gestionale giù. Anche l'assenza di response (rete/timeout)
          // va trattata come guasto, non come "nessun acquisto": confonderli
          // è il difetto originale.
          const st = e?.response?.status;
          if (st === 503 || st === 502 || !e?.response) erpNonRaggiungibile = true;
          return { product: p, movimenti: [] as any[], caricamento: null, fuori: null };
        }
      }));
      let movByProduct = await chiedi();
      // Al più ~90 s: poi si dice che il gestionale non risponde.
      for (let tentativo = 0; tentativo < 45; tentativo++) {
        const inCorso = movByProduct.some(m => m.caricamento && (m.caricamento.stato === 'caricamento' || m.caricamento.in_corso));
        if (!inCorso) break;
        setCaricandoAcquisti(true);
        await new Promise(res => setTimeout(res, 2000));
        movByProduct = await chiedi();
      }
      setCaricandoAcquisti(false);
      if (movByProduct.some(m => m.caricamento && m.caricamento.stato !== 'pronto')) erpNonRaggiungibile = true;
      for (const m of movByProduct) {
        if (m.fuori && (!fuoriFinestraServer || m.fuori > fuoriFinestraServer)) fuoriFinestraServer = m.fuori;
      }

      if (erpNonRaggiungibile) {
        setSearchError('Gestionale non raggiungibile: non è possibile verificare i tuoi acquisti in questo momento. Riprova tra qualche minuto.');
        setResults([]);
        setQuantitaReseMap({});
        return;
      }

      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - finestraMesi);

      const rows: SearchResultRow[] = [];
      let scartatoPiuRecente: string | null = null;
      for (const { product: p, movimenti } of movByProduct) {
        for (const m of movimenti) {
          if (!m.data) continue;
          if (new Date(m.data) < cutoff) {
            // L'ERP restituisce dal primo del mese, quindi qualche riga cade
            // appena fuori: tenerne la più recente per poterlo spiegare.
            if (!scartatoPiuRecente || m.data > scartatoPiuRecente) scartatoPiuRecente = m.data;
            continue;
          }
          const docKey = `${m.docTipork}-${m.docAnno}-${m.docSerie}-${m.docNumdoc}`;
          rows.push({
            sku: p.sku,
            dtkCodice: p.dtkCodice || p.sku,
            title: p.title,
            brand: p.brand || '',
            category: p.category || 'altro',
            codiceOem: codiceOemDaMostrare(p, cercato) || p.sku,
            docTipork: m.docTipork,
            docAnno: m.docAnno,
            docSerie: m.docSerie,
            docNumdoc: m.docNumdoc,
            docTipoLabel: m.docTipoLabel || 'Documento',
            docKey,
            // Chiave e numero del D.D.T. li decide il server, uguali a quelli di
            // "Reso da Documenti": così carrello e già-reso vedono una sola
            // riga per consegna (audit RMA #07, #20).
            docNumeroLabel: m.docNumeroLabel || m.orderId,
            orderId: m.orderId,
            orderDate: m.data,
            qtyDelivered: m.quantita,
            unitPrice: m.prezzoUnitario,
          });
        }
      }
      rows.sort((a, b) => (a.orderDate < b.orderDate ? 1 : -1));

      // Quantità già richieste in reso per ciascun documento coinvolto
      const uniqueDocs = Array.from(new Map(rows.map(r => [r.docKey, r])).values());
      const qtyRese: Record<string, Record<string, number>> = {};
      await Promise.all(uniqueDocs.map(async (d) => {
        try {
          qtyRese[d.docKey] = await fetchQuantitaRese(d.docTipork, d.docAnno, d.docSerie, d.docNumdoc);
        } catch { qtyRese[d.docKey] = {}; }
      }));

      setResults(rows);
      setQuantitaReseMap(qtyRese);
      const fuori = [scartatoPiuRecente, fuoriFinestraServer].filter(Boolean).sort().pop() ?? null;
      if (rows.length === 0 && fuori) setUltimoFuoriFinestra(fuori);
    } catch (e: any) {
      // Rete di sicurezza: i fallimenti attesi sono già gestiti sopra, uno per
      // prodotto e uno per documento. Quel che arriva qui è inatteso, e va
      // comunque detto: prima finiva in una lista vuota indistinguibile da
      // "nessun risultato".
      setSearchError(e?.response?.data?.detail ?? 'Ricerca non riuscita. Riprova.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  /** Fase 1: risolve il testo digitato in prodotti di catalogo, poi delega. */
  const runSearch = async (overrideQuery?: string) => {
    const q = (overrideQuery ?? query).trim();
    if (q.length < 2) return;
    setSuggestOpen(false);
    setLoading(true);
    setSearched(true);
    setSearchError(null);
    try {
      // Ricerca ampia (codice, nome, compatibilità, marca): questa è la
      // ricerca libera, e il segnaposto dichiara di accettare anche il nome.
      const prodRes = await productService.list({ q, limit: 15 });
      await loadMovimenti(prodRes.items, q);
    } catch (e: any) {
      // Fallimento della risoluzione di catalogo: senza questo l'errore
      // diventava una lista vuota, come se il codice non esistesse.
      setSearchError(e?.response?.data?.detail ?? 'Ricerca non riuscita. Riprova.');
      setResults([]);
      setLoading(false);
    }
  };

  const pickSuggestion = (p: any) => {
    skipSuggestRef.current = true;
    setQuery(p.dtkCodice || p.sku);
    setSuggestions([]);
    setSuggestOpen(false);
    // Il prodotto è già quello scelto dall'utente: si va diritti all'ERP.
    // Prima si rifaceva una ricerca di catalogo passando il codice come
    // stringa, con due modi di perdere l'articolo appena selezionato — la
    // ricerca libera non guarda ``dtkCodice``/``oemCodes`` (quindi un codice
    // trovato dal dropdown poteva non trovarsi più), e il limite di 15
    // risultati con ordinamento di catalogo poteva escluderlo comunque.
    loadMovimenti([p]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestOpen && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, -1)); return; }
      if (e.key === 'Escape') { setSuggestOpen(false); return; }
      if (e.key === 'Enter' && selectedIdx >= 0) { e.preventDefault(); pickSuggestion(suggestions[selectedIdx]); return; }
    }
    if (e.key === 'Enter') runSearch();
  };

  const displayResults = useMemo(() => results, [results]);

  // Soglia mostrata nei messaggi. Coerente con il filtro applicato sopra,
  // che usa la stessa aritmetica ``setMonth``.
  const cutoffLabel = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - finestraMesi);
    return d.toISOString().slice(0, 10);
  }, [finestraMesi]);

  return (
    <div data-testid="rma-search-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/area-clienti/rma" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna a RMA / Resi
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
            <Search className="w-5 h-5 text-[hsl(214,58%,41%)]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Reso per Prodotto</h1>
            <p className="text-sm text-muted-foreground">Cerca il prodotto che vuoi rendere per codice, OEM o nome.</p>
          </div>
        </div>

        {/* Barra ricerca con autocompletamento */}
        <div className="rounded-xl border bg-white p-5 mb-6">
          <div className="flex items-center gap-3" ref={containerRef}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => { if (suggestions.length > 0) setSuggestOpen(true); }}
                placeholder="Cerca per codice DTK, codice OEM o nome prodotto..."
                className="w-full h-11 pl-10 pr-10 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                autoFocus
                autoComplete="off"
                data-testid="rma-search-input"
              />
              {suggestLoading && (
                /* La centratura verticale sta sul contenitore, non sull'icona.
                   ``animate-spin`` definisce solo il keyframe ``to``, quindi il
                   browser interpola partendo dal transform calcolato
                   dell'elemento: con ``-translate-y-1/2`` sulla stessa icona
                   l'animazione andava da ``translateY(-50%)`` a
                   ``rotate(360deg)``, cioè l'icona scendeva invece di girare e
                   a ogni giro risaliva di scatto. Non riunirli. */
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  data-testid="rma-search-suggest-loading"
                >
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </span>
              )}

              {suggestOpen && (
                <div
                  className="absolute z-40 left-0 right-0 top-[calc(100%+4px)] rounded-lg border bg-white shadow-xl overflow-hidden"
                  data-testid="rma-search-suggestions"
                >
                  {suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-muted-foreground" data-testid="rma-search-suggestions-empty">
                      Nessun articolo trovato nel catalogo.
                    </div>
                  ) : suggestions.map((p, idx) => (
                    <button
                      // ``expandOemMatches`` può restituire lo stesso prodotto
                      // una volta per codice OEM corrispondente: la sola sku
                      // non basta come chiave.
                      key={`${p.sku}-${p.matchedOemCode ?? idx}`}
                      type="button"
                      onMouseEnter={() => setSelectedIdx(idx)}
                      onClick={() => pickSuggestion(p)}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b last:border-b-0 transition-colors ${idx === selectedIdx ? 'bg-[hsl(var(--dtk-canvas))]' : 'bg-white hover:bg-[hsl(var(--dtk-canvas))]'}`}
                      data-testid={`rma-search-suggestion-${idx}`}
                    >
                      <div className="w-7 h-7 rounded-md bg-secondary/60 flex items-center justify-center flex-shrink-0">
                        <Package className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11px] font-bold text-[hsl(214,58%,41%)]">{p.dtkCodice || p.sku}</span>
                          {p.brand && <span className="text-[10px] font-bold border px-1.5 py-0.5 rounded bg-secondary/60">{p.brand}</span>}
                          {codiceOemDaMostrare(p, query) && <span className="font-mono text-[10px] text-muted-foreground">OEM: {codiceOemDaMostrare(p, query)}</span>}
                        </div>
                        <div className="text-xs truncate">{p.title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => runSearch()}
              disabled={query.trim().length < 2 || loading}
              className="h-11 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              data-testid="rma-search-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Cerca
            </button>
          </div>
          {query.length > 0 && query.trim().length < 2 && (
            <p className="text-xs text-muted-foreground mt-2">Digita almeno 2 caratteri per cercare.</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Vengono mostrati solo gli acquisti degli ultimi {finestraMesi} mesi.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="rma-search-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {caricandoAcquisti
              ? `Stiamo caricando i tuoi acquisti degli ultimi ${finestraMesi} mesi: la prima volta può servire fino a un minuto.`
              : 'Ricerca acquisti in corso...'}
          </div>
        )}

        {/* Errore — mai confuso con un risultato vuoto */}
        {!loading && searchError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="rma-search-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{searchError}</span>
          </div>
        )}

        {/* Risultati */}
        {!loading && !searchError && searched && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground mb-3" data-testid="rma-search-summary">
              {displayResults.length > 0
                ? `${displayResults.length} risultat${displayResults.length === 1 ? 'o' : 'i'} trovat${displayResults.length === 1 ? 'o' : 'i'}`
                : ultimoFuoriFinestra
                  ? `Reso non disponibile per acquisti effettuati prima del ${fmtIt(cutoffLabel)}. Il tuo acquisto più recente di questo articolo è del ${fmtIt(ultimoFuoriFinestra)}.`
                  : `Reso non disponibile per acquisti effettuati prima del ${fmtIt(cutoffLabel)}. Questo articolo non risulta acquistato in questo periodo.`
              }
            </div>

            {displayResults.map((p, idx) => {
              const alreadyRequested = quantitaReseMap[p.docKey]?.[p.sku] || 0;
              const inCart = rmaItems.find(i => i.docTipork === p.docTipork && i.docAnno === p.docAnno && i.docSerie === p.docSerie && i.docNumdoc === p.docNumdoc && i.sku === p.sku)?.units.length || 0;
              return (
                <RMAProductCard
                  key={`${p.docKey}-${p.sku}-${idx}`}
                  sku={p.sku}
                  title={p.title}
                  brand={p.brand}
                  quantity={p.qtyDelivered}
                  alreadyRequested={alreadyRequested}
                  unitPrice={p.unitPrice}
                  inCart={inCart}
                  orderDate={p.orderDate}
                  returnWindowMonths={finestraMesi}
                  testId={`rma-search-result-${idx}`}
                  extraMeta={(
                    <div className="flex items-center gap-3 mt-0.5 text-xs flex-wrap">
                      <span className="font-mono text-[11px] font-bold text-[hsl(214,58%,41%)]">DTK: {p.dtkCodice}</span>
                      <span className="text-muted-foreground">{p.docTipoLabel}: <span className="font-mono font-medium text-foreground">{numeroDocumento(p.docNumeroLabel)}</span></span>
                      <span className="text-muted-foreground">Data: {new Date(p.orderDate).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  onAdd={(effectiveMaxQty) => setModalProduct({
                    sku: p.sku,
                    title: p.title,
                    brand: p.brand,
                    category: p.category,
                    dtkCodice: p.dtkCodice,
                    codiceOem: p.codiceOem,
                    orderId: p.orderId,
                    orderDate: p.orderDate,
                    orderStatus: 'consegnato',
                    docTipork: p.docTipork,
                    docAnno: p.docAnno,
                    docSerie: p.docSerie,
                    docNumdoc: p.docNumdoc,
                    maxQty: effectiveMaxQty,
                    qtyPurchased: p.qtyDelivered,
                    unitPrice: p.unitPrice,
                  })}
                />
              );
            })}
          </div>
        )}

        {/* Hint */}
        {!loading && !searched && (
          <div className="rounded-xl border border-dashed bg-[hsl(var(--dtk-canvas))] p-8 text-center">
            <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Cerca il prodotto che desideri rendere. Puoi cercare per codice DTK, codice OEM o nome del prodotto.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              In alternativa, puoi aprire un reso <Link to="/area-clienti/rma/da-documento" className="text-[hsl(214,58%,46%)] hover:underline font-medium">selezionando una fattura</Link>.
            </p>
          </div>
        )}
      </div>

      <AddToRMAModal open={!!modalProduct} onClose={() => setModalProduct(null)} product={modalProduct} />
    </div>
  );
}
